import React, { FC, useCallback, useEffect, useReducer, useState } from 'react';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { action } from 'typesafe-actions';
import { RecurringOrderRepositoryImpl } from '../../domain/RecurringOrder/infrastructure';
import { RecurringOrderServiceImpl } from '../../domain/RecurringOrder/usecases';
import {
  StandingOrder,
  StandingOrderDetails,
  SweepOrder,
  SweepOrderDetails,
} from '../../domain/RecurringOrder/entities';
import { RecurringOrdersReducer } from './RecurringOrdersReducer';
import { defaultState } from './defaultState';
import { RecurringOrdersContext, RecurringOrdersDispatchContext } from './RecurringOrdersContext';
import {
  RecurringOrdersActionEnum,
  RecurringOrdersContextActionTypes,
  RecurringOrdersSubmitActionEnum,
  RecurringOrderTabs,
} from './types';

const RecurringOrdersProvider: FC = ({ children }) => {
  // Get initial state values
  const [state, dispatch] = useReducer(RecurringOrdersReducer, defaultState);
  // Set services state
  const [recurringOrderService, setRecurringOrderService] = useState<any>(null);
  // Set the services and its implementation on provider mount
  useEffect(() => {
    const recurringOrderRepositoryImplInstance = new RecurringOrderRepositoryImpl();
    const recurringOrderServiceInstance = new RecurringOrderServiceImpl(recurringOrderRepositoryImplInstance);
    setRecurringOrderService(recurringOrderServiceInstance);
  }, []);

  // Define api call conditions
  const isStandingOrderSelected = state.selectedRecurringOrdersTab === RecurringOrderTabs.standing;
  const isSweepOrderSelected = state.selectedRecurringOrdersTab === RecurringOrderTabs.sweep;
  const shouldFetchStandingOrders = isStandingOrderSelected && Boolean(recurringOrderService);
  const shouldFetchSweepOrders = isSweepOrderSelected;
  const shouldCancelStandingOrder =
    isStandingOrderSelected &&
    state.recurringOrderAction === RecurringOrdersActionEnum.cancel &&
    state.recurringOrderSubmitAction === RecurringOrdersSubmitActionEnum.cancel &&
    Boolean(state.selectedRecurringOrder);
  const shouldCancelSweepOrder =
    isSweepOrderSelected &&
    state.recurringOrderAction === RecurringOrdersActionEnum.cancel &&
    state.recurringOrderSubmitAction === RecurringOrdersSubmitActionEnum.cancel &&
    Boolean(state.selectedRecurringOrder);
  const shouldFetchStandingOrderDetails =
    isStandingOrderSelected &&
    state.recurringOrderAction === RecurringOrdersActionEnum.viewDetails &&
    Boolean(state.selectedRecurringOrder);
  const shouldFetchSweepOrderDetails =
    isSweepOrderSelected &&
    state.recurringOrderAction === RecurringOrdersActionEnum.viewDetails &&
    Boolean(state.selectedRecurringOrder);

  // Define api calls
  const {
    data: standingOrdersData,
    mutate: standingOrdersRefetch,
    error: standingOrdersError,
    isLoading: isLoadingStandingOrders,
  } = useInvokeSWRService<StandingOrder[]>(
    recurringOrderService?.GetStandingOrders,
    {},
    shouldFetchStandingOrders,
    'recurringOrderService.GetStandingOrders',
  );

  const {
    data: sweepOrdersData,
    mutate: sweepOrdersRefetch,
    error: sweepOrdersError,
    isLoading: isLoadingSweepOrders,
  } = useInvokeSWRService<SweepOrder[]>(
    recurringOrderService?.GetSweepOrders,
    {},
    shouldFetchSweepOrders,
    'recurringOrderService.GetSweepOrders',
  );

  const {
    data: cancelStandingOrderData,
    error: cancelStandingOrderError,
    isLoading: isLoadingCancelStandingOrder,
  } = useInvokeSWRService<{}>(
    recurringOrderService?.CancelStandingOrder,
    {
      id: state?.selectedRecurringOrder?.code,
      successCallback: () => {
        standingOrdersRefetch && standingOrdersRefetch();
      },
    },
    shouldCancelStandingOrder,
    'recurringOrderService.CancelStandingOrder',
  );

  const {
    data: cancelSweepOrderData,
    error: cancelSweepOrderError,
    isLoading: isLoadingCancelSweepOrder,
  } = useInvokeSWRService<{}>(
    recurringOrderService?.CancelSweepOrder,
    {
      id: state?.selectedRecurringOrder?.code,
      successCallback: () => {
        sweepOrdersRefetch && sweepOrdersRefetch();
      },
    },
    shouldCancelSweepOrder,
    'recurringOrderService.CancelSweepOrder',
  );

  const {
    data: standingOrdersDetailsData,
    error: standingOrderDetailsError,
    isLoading: isLoadingStandingOrderDetails,
  } = useInvokeSWRService<StandingOrderDetails>(
    recurringOrderService?.GetStandingOrderDetails,
    { id: state?.selectedRecurringOrder?.code },
    shouldFetchStandingOrderDetails,
    'recurringOrderService.GetStandingOrderDetails',
  );

  const {
    data: sweepOrdersDetailsData,
    error: sweepOrderDetailsError,
    isLoading: isLoadingSweepOrderDetails,
  } = useInvokeSWRService<SweepOrderDetails>(
    recurringOrderService?.GetSweepOrderDetails,
    { id: state?.selectedRecurringOrder?.code },
    shouldFetchSweepOrderDetails,
    'recurringOrderService.GetSweepOrderDetails',
  );

  const onRecurringOrderAction = useCallback((selectedRecurringOrder, recurringOrderAction) => {
    dispatch(
      action(RecurringOrdersContextActionTypes.SetSelectedRecurringOrder, {
        selectedRecurringOrder,
        action: recurringOrderAction,
      }),
    );
  }, []);

  const onDownloadOption = useCallback(type => {
    // TODO call for download, type has download option
  }, []);

  const onSelectRecurringOrdersTab = useCallback(type => {
    dispatch(action(RecurringOrdersContextActionTypes.SetSelectedRecurringOrdersTab, type));
  }, []);

  const onResetSelectedRecurringOrder = useCallback(() => {
    dispatch(action(RecurringOrdersContextActionTypes.ResetSelectedRecurringOder, null));
  }, []);

  const onRecurringOrderSubmitAction = useCallback(type => {
    dispatch(action(RecurringOrdersContextActionTypes.SetRecurringOrderSubmitAction, type));
  }, []);

  return (
    <RecurringOrdersContext.Provider
      value={{
        ...state,
        isStandingOrderSelected,
        standingOrders: standingOrdersData ?? null,
        sweepOrders: sweepOrdersData ?? null,
        isLoadingStandingOrders,
        standingOrdersError,
        isLoadingSweepOrders,
        sweepOrdersError,
        isLoadingCancelRecurringOrder: isStandingOrderSelected
          ? isLoadingCancelStandingOrder
          : isLoadingCancelSweepOrder,
        cancelRecurringOrderError: isStandingOrderSelected ? cancelStandingOrderError : cancelSweepOrderError,
        cancelRecurringOrderData: (isStandingOrderSelected ? cancelStandingOrderData : cancelSweepOrderData) ?? null,
        isLoadingViewDetailsRecurringOrder: isStandingOrderSelected
          ? isLoadingStandingOrderDetails
          : isLoadingSweepOrderDetails,
        viewDetailsRecurringOrderError: isStandingOrderSelected ? standingOrderDetailsError : sweepOrderDetailsError,
        viewRecurringOrderDetailsData:
          (isStandingOrderSelected ? standingOrdersDetailsData : sweepOrdersDetailsData) ?? null,
        onSelectRecurringOrdersTab,
        onRecurringOrderAction,
        onDownloadOption,
        onResetSelectedRecurringOrder,
        onRecurringOrderSubmitAction,
      }}>
      <RecurringOrdersDispatchContext.Provider value={dispatch}>{children}</RecurringOrdersDispatchContext.Provider>
    </RecurringOrdersContext.Provider>
  );
};

export { RecurringOrdersProvider };
